import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import type { AdminAccountLimit } from "../../generic/server/supabase-account-service";
import {
  DEFAULT_SES_CONFIGURATION_SET,
  NO_TRACKING_SES_CONFIGURATION_SET,
} from "../../generic/server/supabase-account-service";
import type { AccountLimitsUpdate } from "../hooks/use-save-account-limits";

const CUSTOM = "custom" as const;

const KNOWN_SES_CONFIGURATION_SETS: string[] = [
  DEFAULT_SES_CONFIGURATION_SET,
  NO_TRACKING_SES_CONFIGURATION_SET,
];

export type AccountSectionProps = {
  account: AdminAccountLimit;
  onSave: (update: AccountLimitsUpdate) => Promise<void | undefined>;
};

export const AccountGeneralSection: FC<AccountSectionProps> = ({ account, onSave }) => {
  const [businessName, setBusinessName] = useState(account.business_name);
  const [usage, setUsage] = useState(account.usage);
  const [churned, setChurned] = useState(account.churned);
  const [sesMode, setSesMode] = useState(
    KNOWN_SES_CONFIGURATION_SETS.includes(account.ses_configuration_set)
      ? account.ses_configuration_set
      : CUSTOM,
  );
  const [customSes, setCustomSes] = useState(
    KNOWN_SES_CONFIGURATION_SETS.includes(account.ses_configuration_set)
      ? ""
      : account.ses_configuration_set,
  );
  const [saving, setSaving] = useState(false);

  const sesConfigurationSet = sesMode === CUSTOM ? customSes.trim() : sesMode;

  const save = async (): Promise<void> => {
    setSaving(true);
    try {
      await onSave({
        business_name: businessName,
        usage,
        churned,
        ses_configuration_set: sesConfigurationSet,
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper id="general" elevation={24} sx={{ p: 4 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        General
      </Typography>
      <Stack spacing={2}>
        <TextField
          label="Business Name"
          variant="standard"
          fullWidth
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
          helperText="The Business Name (used for grouping Pendo)"
        />
        <FormControl variant="standard" fullWidth>
          <InputLabel id="account-usage-label">Account Usage</InputLabel>
          <Select
            labelId="account-usage-label"
            label="Account Usage"
            value={usage}
            onChange={(e) => setUsage(e.target.value)}
          >
            <MenuItem value="CLIENT">Client</MenuItem>
            <MenuItem value="DEMO">Demo</MenuItem>
            <MenuItem value="TESTING">Testing</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          control={<Checkbox checked={churned} onChange={(e) => setChurned(e.target.checked)} />}
          label="Churned"
        />
        <FormControl variant="standard" fullWidth>
          <InputLabel id="ses-configuration-set-label">SES Configuration Set</InputLabel>
          <Select
            labelId="ses-configuration-set-label"
            label="SES Configuration Set"
            value={sesMode}
            onChange={(e) => setSesMode(e.target.value)}
          >
            <MenuItem value={DEFAULT_SES_CONFIGURATION_SET}>Default</MenuItem>
            <MenuItem value={NO_TRACKING_SES_CONFIGURATION_SET}>No Tracking Pixel</MenuItem>
            <MenuItem value={CUSTOM}>Custom</MenuItem>
          </Select>
        </FormControl>
        {sesMode === CUSTOM && (
          <TextField
            label="Custom Configuration Set"
            variant="standard"
            fullWidth
            value={customSes}
            onChange={(e) => setCustomSes(e.target.value)}
            helperText="Name of an AWS SES configuration set, e.g. for a dedicated IP pool"
          />
        )}
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            data-analytics-id="ce-admin-save-general"
            variant="contained"
            onClick={() => void save()}
            disabled={saving || sesConfigurationSet === ""}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
};
